import { Injectable, inject, EnvironmentInjector, runInInjectionContext, OnDestroy } from '@angular/core';
import { Firestore, doc, onSnapshot, collection, getCountFromServer } from '@angular/fire/firestore';
import { Auth, authState } from '@angular/fire/auth';
import { BehaviorSubject, Observable, Subscription, distinctUntilChanged, filter, firstValueFrom, map, switchMap, of, combineLatest } from 'rxjs';
import { OrganizationService } from './organization.service';
import { PlansService, SubscriptionPlan, PlanLimits } from './plans.service';
import { PermissionsService } from './permissions.service';
import { environment } from '../../../environments/environment';

export type SubscriptionStatus =
    | 'active'
    | 'trialing'
    | 'cancelled'
    | 'expired'
    | 'pending'
    | 'payment_failed'
    | 'non_renewing'
    | 'onboarding' // created but has not finished setup, so no trial yet
    | 'blocked'    // no subscription at all or past grace period
    | 'grace';     // expired but within 3-day grace period

export interface SubscriptionState {
    status: SubscriptionStatus;
    planId: string | null;
    planName: string | null;
    limits: PlanLimits | null;
    trialEndDate: Date | null;
    trialDaysRemaining: number | null;
    graceDaysRemaining: number | null;
    isActive: boolean;       // can use the app fully
    isTrial: boolean;
    isTrialCancelled: boolean; // trial is still running but has been cancelled (won't renew)
    isBlocked: boolean;      // cannot use feature pages
    isGrace: boolean;        // expired but within grace period
    isOnboarding: boolean;   // still in setup — the trial has not started yet
    countryCode: string | null;
    initialized: boolean;    // false until the first real Firestore emission has been processed
    /**
     * Combined count of Planning Center-synced people and manually-imported
     * contacts. Null until the first count completes — treat null as "unknown",
     * not zero.
     */
    congregationSize: number | null;
}

const GRACE_PERIOD_DAYS = 3;

const DEFAULT_STATE: SubscriptionState = {
    status: 'blocked',
    planId: null,
    planName: null,
    limits: null,
    trialEndDate: null,
    trialDaysRemaining: null,
    graceDaysRemaining: null,
    isActive: false,
    isTrial: false,
    isTrialCancelled: false,
    isBlocked: true,
    isGrace: false,
    isOnboarding: false,
    countryCode: null,
    initialized: false,
    congregationSize: null,
};

@Injectable({
    providedIn: 'root'
})
export class SubscriptionStateService implements OnDestroy {
    private firestore = inject(Firestore);
    private auth = inject(Auth);
    private injector = inject(EnvironmentInjector);
    private organizationService = inject(OrganizationService);
    private plansService = inject(PlansService);
    private permissionsService = inject(PermissionsService);

    private stateSubject = new BehaviorSubject<SubscriptionState>(DEFAULT_STATE);
    private orgUnsubscribe?: () => void;
    private authSub?: Subscription;
    private plansCache: SubscriptionPlan[] = [];
    private plansUnsubscribe?: () => void;
    private initialized = false;

    /** Full subscription state observable */
    readonly state$ = this.stateSubject.asObservable();

    /** Convenience observables */
    readonly isActive$ = this.state$.pipe(map(s => s.isActive), distinctUntilChanged());
    readonly isTrial$ = this.state$.pipe(map(s => s.isTrial), distinctUntilChanged());
    readonly isBlocked$ = this.state$.pipe(map(s => s.isBlocked), distinctUntilChanged());
    readonly isGrace$ = this.state$.pipe(map(s => s.isGrace), distinctUntilChanged());
    readonly status$ = this.state$.pipe(map(s => s.status), distinctUntilChanged());
    readonly limits$ = this.state$.pipe(map(s => s.limits), distinctUntilChanged());
    readonly trialDaysRemaining$ = this.state$.pipe(map(s => s.trialDaysRemaining), distinctUntilChanged());
    readonly countryCode$ = this.state$.pipe(map(s => s.countryCode), distinctUntilChanged());
    readonly isOverCongregationCap$ = this.state$.pipe(
        map(s => s.congregationSize !== null && s.limits?.maxCongregationSize != null &&
            s.congregationSize >= s.limits.maxCongregationSize),
        distinctUntilChanged()
    );
    /**
     * Website chat and API access are granted to any organisation on an
     * active trial, regardless of the plan's flag — mirrors the backend's
     * planIncludes() trial bypass. Read-only pages (Website Chat, API Access)
     * use these to decide whether their mutating actions are enabled.
     */
    readonly hasWebChat$ = this.state$.pipe(
        map(s => s.isTrial || s.limits?.includesWebChat === true),
        distinctUntilChanged()
    );
    readonly hasApiAccess$ = this.state$.pipe(
        map(s => s.isTrial || s.limits?.includesApiAccess === true),
        distinctUntilChanged()
    );
    readonly hasAi$ = this.state$.pipe(
        map(s => s.isTrial || s.limits?.includesAi === true),
        distinctUntilChanged()
    );
    /** SMS integrations: linking an SMS provider for WhatsApp fallback and SMS replies. */
    readonly hasSms$ = this.state$.pipe(
        map(s => s.isTrial || s.limits?.includesSms === true),
        distinctUntilChanged()
    );
    /**
     * Conversation workflows and list automations. Enterprise only, and
     * nobody at all while environment.workflowsEnabled is off.
     */
    readonly hasWorkflows$ = this.state$.pipe(
        map(s => environment.workflowsEnabled && (s.isTrial || s.limits?.includesWorkflows === true)),
        distinctUntilChanged()
    );

    /**
     * The workflows entitlement once the subscription has actually loaded.
     * `hasWorkflows$` starts from the default state, which says "no" — fine
     * for a live binding, wrong for a one-off check made during page load.
     */
    hasWorkflowsOnceLoaded(): Promise<boolean> {
        return firstValueFrom(this.state$.pipe(
            filter(s => s.initialized),
            map(s => environment.workflowsEnabled && (s.isTrial || s.limits?.includesWorkflows === true))
        ));
    }

    /** The org currently being listened to, so refreshCongregationSize() knows what to count. */
    private currentOrgId: string | null = null;

    constructor() {
        this.init();
    }

    private init(): void {
        if (this.initialized) return;
        this.initialized = true;

        // Load plans cache
        this.plansUnsubscribe = this.plansService.getActivePlansRealtime((plans) => {
            this.plansCache = plans;
            // Re-evaluate state when plans change
            this.refreshState();
        });

        // Listen for auth changes → listen to org document
        const authState$ = authState(this.auth);
        this.authSub = authState$.pipe(
            switchMap(user => {
                if (!user) return of(null);
                return this.organizationService.getUserOrganization(user.uid);
            })
        ).subscribe({
            next: (orgInfo: any) => {
                // Clean up previous org listener
                if (this.orgUnsubscribe) {
                    this.orgUnsubscribe();
                    this.orgUnsubscribe = undefined;
                }

                if (!orgInfo?.organizationId) {
                    this.currentOrgId = null;
                    this.stateSubject.next({ ...DEFAULT_STATE, initialized: true });
                    return;
                }

                this.listenToOrganization(orgInfo.organizationId);
            },
            error: () => {
                this.currentOrgId = null;
                this.stateSubject.next({ ...DEFAULT_STATE, initialized: true });
            }
        });
    }

    private listenToOrganization(orgId: string): void {
        this.currentOrgId = orgId;
        const orgRef = doc(this.firestore, 'organizations', orgId);

        this.orgUnsubscribe = runInInjectionContext(this.injector, () =>
            onSnapshot(orgRef, (docSnap) => {
                if (!docSnap.exists()) {
                    this.stateSubject.next({ ...DEFAULT_STATE, initialized: true });
                    return;
                }

                const orgData = docSnap.data();
                const subscription = orgData?.['subscription'];
                const countryCode = orgData?.['countryCode'] || null;

                this.evaluateState(subscription, countryCode);
            }, () => {
                this.stateSubject.next({ ...DEFAULT_STATE, initialized: true });
            })
        );

        // Once per org/session, not on every org-doc write — cheap aggregation
        // reads, but there's no need to repeat them on every snapshot.
        void this.refreshCongregationSize();
    }

    /**
     * Recompute the org's combined congregation size (Planning Center people +
     * manually-imported contacts) and merge it into the current state.
     *
     * Public so screens that change the count directly — Manual Imports, after
     * a successful import — can ask for a fresh read without waiting for
     * whatever next touches the org document.
     */
    async refreshCongregationSize(): Promise<void> {
        const orgId = this.currentOrgId;
        if (!orgId) return;

        try {
            const [pcoPeople, contacts] = await runInInjectionContext(this.injector, () => Promise.all([
                getCountFromServer(collection(this.firestore, `organizations/${orgId}/pco_people`)),
                getCountFromServer(collection(this.firestore, `organizations/${orgId}/contacts`))
            ]));

            // The org may have switched (or the listener torn down) while the
            // count was in flight — don't apply a stale result to a new org.
            if (this.currentOrgId !== orgId) return;

            this.stateSubject.next({
                ...this.stateSubject.getValue(),
                congregationSize: pcoPeople.data().count + contacts.data().count,
            });
        } catch {
            // Fail quiet, same as the existing plan-selection page's count —
            // congregationSize stays whatever it was (null on first load).
        }
    }

    private refreshState(): void {
        // Re-emit current state to trigger re-evaluation with updated plans
        const current = this.stateSubject.getValue();
        if (current.planId) {
            const plan = this.plansCache.find(p => p.id === current.planId);
            if (plan) {
                this.stateSubject.next({
                    ...current,
                    limits: plan.limits || null,
                });
            }
        }
    }

    private evaluateState(subscription: any, countryCode: string | null): void {
        if (!subscription) {
            this.stateSubject.next({ ...DEFAULT_STATE, countryCode, initialized: true });
            return;
        }

        const now = new Date();
        const plan = this.plansCache.find(p => p.id === subscription.planId);
        const limits = plan?.limits || null;
        const planName = plan?.name || subscription.planName || subscription.planId || null;

        // Parse dates
        const trialEndDate = subscription.trialEndDate?.toDate?.() || null;
        const nonRenewDate = subscription.nonRenewDate?.toDate?.() || null;
        const willCancelOn = subscription.willCancelOn?.toDate?.() || null;
        const cancelledAt = subscription.cancelledAt?.toDate?.() || null;
        const nextBillingDate = subscription.nextBillingDate?.toDate?.() || null;

        // Calculate trial days remaining
        let trialDaysRemaining: number | null = null;
        if (trialEndDate && now < trialEndDate) {
            trialDaysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        }

        // Determine effective end date (for non-renewing/cancelled subs)
        const effectiveEndDate = nonRenewDate || willCancelOn || null;

        // Calculate grace period
        let graceDaysRemaining: number | null = null;
        let isInGracePeriod = false;
        if (effectiveEndDate && now >= effectiveEndDate) {
            const graceEnd = new Date(effectiveEndDate.getTime() + GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000);
            if (now < graceEnd) {
                isInGracePeriod = true;
                graceDaysRemaining = Math.ceil((graceEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            }
        }

        // Evaluate subscription status
        let status: SubscriptionStatus;
        let isActive = false;
        let isTrial = false;
        let isTrialCancelled = false;
        let isBlocked = false;
        let isGrace = false;
        let isOnboarding = false;

        switch (subscription.status) {
            case 'active':
                status = 'active';
                isActive = true;
                break;

            case 'trialing':
                if (trialEndDate && now >= trialEndDate) {
                    // Trial has ended but status wasn't updated
                    status = 'blocked';
                    isBlocked = true;
                } else if (cancelledAt || subscription.willNotRenew) {
                    // Trial cancelled (via app or Paystack dashboard) — still usable until trial end
                    status = 'trialing';
                    isTrial = true;
                    isTrialCancelled = true;
                    isActive = true;
                } else {
                    status = 'trialing';
                    isTrial = true;
                    isActive = true;
                }
                break;

            case 'non_renewing':
                if (effectiveEndDate && now < effectiveEndDate) {
                    // Still within paid period
                    status = 'non_renewing';
                    isActive = true;
                } else if (isInGracePeriod) {
                    status = 'grace';
                    isGrace = true;
                    isActive = true; // limited access during grace
                } else {
                    status = 'blocked';
                    isBlocked = true;
                }
                break;

            case 'cancelled':
                if (effectiveEndDate && now < effectiveEndDate) {
                    status = 'cancelled';
                    isActive = true; // still within paid period
                } else if (isInGracePeriod) {
                    status = 'grace';
                    isGrace = true;
                    isActive = true;
                } else {
                    status = 'blocked';
                    isBlocked = true;
                }
                break;

            case 'payment_failed':
                // Allow grace period for payment failures
                status = 'payment_failed';
                isActive = true; // give them time to fix payment
                break;

            case 'expired':
                if (isInGracePeriod) {
                    status = 'grace';
                    isGrace = true;
                    isActive = true;
                } else {
                    status = 'blocked';
                    isBlocked = true;
                }
                break;

            case 'pending':
                status = 'pending';
                isBlocked = true;
                break;

            case 'onboarding':
                // Setup isn't finished, so the trial hasn't started. Still gated
                // out of the feature pages, but this is not the same thing as
                // being blocked and the banner must not say it is.
                status = 'onboarding';
                isBlocked = true;
                isOnboarding = true;
                break;

            default:
                status = 'blocked';
                isBlocked = true;
                break;
        }

        this.stateSubject.next({
            status,
            planId: subscription.planId || null,
            planName,
            limits,
            trialEndDate,
            trialDaysRemaining,
            graceDaysRemaining,
            isActive,
            isTrial,
            isTrialCancelled,
            isBlocked,
            isGrace,
            isOnboarding,
            countryCode,
            initialized: true,
            // Not derived from the org doc — carried forward from whatever
            // refreshCongregationSize() last computed for this org.
            congregationSize: this.stateSubject.getValue().congregationSize,
        });
    }

    /** Check if the user can perform a specific action based on subscription state */
    canPerformAction(action: 'send' | 'createAutomation' | 'createSchedule' | 'createTemplate' | 'addTeamMember'): boolean {
        const state = this.stateSubject.getValue();
        return state.isActive;
    }

    /** Get the current plan limits */
    getCurrentLimits(): PlanLimits | null {
        return this.stateSubject.getValue().limits;
    }

    /** Get the current subscription state synchronously */
    getCurrentState(): SubscriptionState {
        return this.stateSubject.getValue();
    }

    ngOnDestroy(): void {
        this.orgUnsubscribe?.();
        this.authSub?.unsubscribe();
        this.plansUnsubscribe?.();
    }
}
